import { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Alert from "@mui/material/Alert";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Pagination from "@mui/material/Pagination";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import type { AvailableNetwork } from "../../types/pdm.types";
import config from "../../data/config";

type BridgeNetworkSelectorProps = {
  availableNetworks: AvailableNetwork[];
  selectedNetwork: string;
  value: string;
  issuerId: string;
  onAccept: (value: string) => void;
  onCancel: () => void;
};

type BridgeItem = {
  id: string;
  deploymentId: string;
  bridgeType: string;
  name: string;
  address: string;
  network: string;
  description: string;
  tokenAddress: string;
  issuerId: string;
  createdAt: string;
  updatedAt: string;
};

type BridgesResponse = {
  items: BridgeItem[];
  total: number;
  offset: number;
  limit: number;
};

const PAGE_SIZE = 20;

export function BridgeNetworkSelector(props: BridgeNetworkSelectorProps) {
  const { issuerId, onAccept, onCancel } = props;

  const [page, setPage] = useState(1);
  const [bridges, setBridges] = useState<BridgeItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bridgesById, setBridgesById] = useState<Record<string, BridgeItem>>({});
  const [nameFilter, setNameFilter] = useState("");

  const totalPages = useMemo(
    () => (total > 0 ? Math.ceil(total / PAGE_SIZE) : 0),
    [total],
  );

  const filteredBridges = useMemo(() => {
    const query = nameFilter.trim().toLowerCase();
    if (!query) {
      return bridges;
    }
    return bridges.filter((bridge) =>
      bridge.name.toLowerCase().includes(query),
    );
  }, [bridges, nameFilter]);

  const fetchBridges = useCallback(
    async (currentPage: number) => {
      const offset = (currentPage - 1) * PAGE_SIZE;
      setLoading(true);
      setError(null);
      try {
        const baseUrl = config.PDM_API_BASE_URL.replace(
          /\/$/,
          "",
        );
        if (!baseUrl) {
          throw new Error("Abstraction layer API base URL is not configured.");
        }
        const url = new URL(`${baseUrl}/bridges`);
        url.search = new URLSearchParams({
          offset: String(offset),
          limit: String(PAGE_SIZE),
          bridgeType: "dstoken-bridge",
          issuerId,
        }).toString();
        const response = await fetch(url.toString(), {
          method: "GET",
          headers: {
            accept: "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to load bridges (status ${response.status})`);
        }

        const data = (await response.json()) as BridgesResponse;

        setBridges(data.items);
        setTotal(data.total);
        setBridgesById((prev) => {
          const next = { ...prev };
          for (const item of data.items) {
            next[item.id] = item;
          }
          return next;
        });
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to load bridges list.",
        );
      } finally {
        setLoading(false);
      }
    },
    [issuerId],
  );

  useEffect(() => {
    if (!issuerId) {
      setBridges([]);
      setTotal(0);
      return;
    }
    void fetchBridges(page);
  }, [fetchBridges, issuerId, page]);

  const handleToggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleAccept = () => {
    const selected: BridgeItem[] = [];
    selectedIds.forEach((id) => {
      const item = bridgesById[id];
      if (item) {
        selected.push(item);
      }
    });

    const mapped = selected.map((item) => ({
      network: item.network,
      bridgeAddress: item.address,
    }));

    const jsonValue = JSON.stringify(mapped, null, 2);
    onAccept(jsonValue);
  };

  const showEmptyState = !loading && !error && issuerId && bridges.length === 0;
  const showNoMatches =
    !loading &&
    !error &&
    bridges.length > 0 &&
    filteredBridges.length === 0 &&
    Boolean(nameFilter.trim());

  return (
    <>
      <DialogContent dividers>
        {!issuerId && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Enter a valid DS token address in the parameters form to load
            associated bridges.
          </Alert>
        )}

        {issuerId && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Showing bridges for the current DS token issuer.
          </Typography>
        )}

        {issuerId && (
          <TextField
            label="Filter by name"
            size="small"
            fullWidth
            value={nameFilter}
            onChange={(event) => setNameFilter(event.target.value)}
            sx={{ mb: 2 }}
          />
        )}

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  if (issuerId) {
                    void fetchBridges(page);
                  }
                }}
              >
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {loading && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              py: 3,
            }}
          >
            <CircularProgress size={22} />
            <Typography variant="body2" color="text.secondary">
              Loading bridges…
            </Typography>
          </Box>
        )}

        {showEmptyState && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No bridges were found for this issuer.
          </Typography>
        )}

        {showNoMatches && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No bridges match this name filter.
          </Typography>
        )}

        {!loading && !error && filteredBridges.length > 0 && (
          <>
            <List
              dense
              disablePadding
              sx={{
                maxHeight: 320,
                overflowY: "auto",
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
              }}
            >
              {filteredBridges.map((bridge) => {
                const labelId = `bridge-checkbox-${bridge.id}`;
                const checked = selectedIds.has(bridge.id);
                return (
                  <ListItem
                    key={bridge.id}
                    disablePadding
                    secondaryAction={null}
                  >
                    <ListItemButton
                      role={undefined}
                      onClick={() => handleToggle(bridge.id)}
                      dense
                    >
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={checked}
                          tabIndex={-1}
                          disableRipple
                          inputProps={{ "aria-labelledby": labelId }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        id={labelId}
                        primary={bridge.name}
                        secondary={
                          <Box sx={{ display: "flex", flexDirection: "column" }}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                            >
                              Network: {bridge.network}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ wordBreak: "break-all" }}
                            >
                              Address: {bridge.address}
                            </Typography>
                            {bridge.description && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 0.5 }}
                              >
                                {bridge.description}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>

            {totalPages > 1 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 2,
                }}
              >
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  disabled={loading}
                  size="small"
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          onClick={handleAccept}
          variant="contained"
          disabled={selectedIds.size === 0}
        >
          Accept
        </Button>
      </DialogActions>
    </>
  );
}

